// pipe: snake_case token → "Title Case" label. Shared by the ops stage labels and the lifecycle
// transition affordance so neither renders a raw token (e.g. `in_processing` → "In Processing").
// This is DISPLAY ONLY — the raw token is always kept for API calls / server SSOT. Pure (no
// RUNTIME deps — the only import is a TYPE, erased at build), so it is safe to import from the
// happy-dom-tested pure view seams.
import type { useI18n } from '@affine/i18n';

/** The i18n wrapper, as every pure view seam receives it. Declared ONCE here rather than
 *  re-derived in each seam. */
export type Translate = ReturnType<typeof useI18n>;

/**
 * THE dynamic-key resolver: localize a SERVER-supplied token through its i18n key, degrading to
 * the humanized token when the key is not translated.
 *
 * The wrapper returns the raw KEY for an unknown one, which is what makes the fallback detectable
 * — so a token the server appends later renders readably ("Shipped To Refiner") instead of blank
 * or as a raw `shipped_to_refiner`. `stageLabel` is this function specialized to stage tokens, and
 * the Item Passport's event / actor / gap / relation / source labels are its other consumers; none
 * of them carries a second copy of this three-line rule.
 *
 * `args` is passed straight through for the keys whose message interpolates (`{{…}}`) — omit it
 * entirely rather than passing an empty object, so a no-argument key resolves identically to a
 * plain `t[key]()` call.
 *
 * (`reports/report-labels.ts`'s `reportLabel` is the same shape with a different fallback
 * contract — it falls back to a server-supplied English STRING, not to a humanized token — so it
 * stays its own function on purpose.)
 */
export function tokenLabel(
  t: Translate,
  key: string,
  token: string,
  args?: Record<string, string>
): string {
  // The wrapper's dynamic-key accessor IS `I18nMethod.t.bind(null, key)`, so it takes the same
  // interpolation options the static accessors do — its `{ [unknownKey: string]: () => string }`
  // index signature is simply narrower than the runtime (see the `I18n['com.affine.xxx']({ arg1 })`
  // example on the wrapper itself). Widening it HERE, once, is why every call site keeps the one
  // accessor idiom that both the app and every spec's i18n mock already speak.
  const label: string = args
    ? (t[key] as (a: Record<string, string>) => string)(args)
    : t[key]();
  return label === key ? humanizeToken(token) : label;
}

/** The legacy ROMS customer number ("CN#") off a row's `legacy_roms_id` tag ("cust:92387" →
 *  "92387"). Pure + shared (Customers list, Customer 360, order detail) — display only; the
 *  entity UUID stays the real key. Null when the row carries no legacy tag. */
export function legacyCn(data: Record<string, unknown>): string | null {
  const v = data.legacy_roms_id;
  if (typeof v !== 'string') return null;
  const m = /^cust:(\d+)$/.exec(v);
  return m ? m[1] : null;
}

/** The legacy ROMS job identity off an order's `legacy_roms_id` tag ("job:92387:112139" →
 *  { cn: "92387", jobNo: "112139" }). Pure, display only. */
export function legacyJob(
  data: Record<string, unknown>
): { cn: string; jobNo: string } | null {
  const v = data.legacy_roms_id;
  if (typeof v !== 'string') return null;
  const m = /^job:(\d+):(.+)$/.exec(v);
  return m ? { cn: m[1], jobNo: m[2] } : null;
}

export function humanizeToken(token: string): string {
  return token
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Collapse a title that is one phrase repeated back-to-back ("Inbound Unsorted Inbound Unsorted
 *  Inbound Unsorted" → "Inbound Unsorted") — a ROMS import artifact where the source glued the
 *  same text from two fields. Pure, display only; anything non-repetitive passes through. */
export function collapseRepeatedTitle(title: string): string {
  const words = title.trim().split(/\s+/);
  for (let k = 1; k <= words.length / 2; k++) {
    if (words.length % k !== 0) continue;
    const head = words.slice(0, k).join(' ');
    let repeats = true;
    for (let i = k; i < words.length; i += k) {
      if (words.slice(i, i + k).join(' ') !== head) {
        repeats = false;
        break;
      }
    }
    if (repeats) return head;
  }
  return title.trim();
}

/** A scalar for display: an RFC3339 timestamp renders date-only; everything else verbatim. */
export function displayValue(v: string): string {
  return /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})$/.test(v)
    ? v.slice(0, 10)
    : v;
}
